import { db } from '../../config/firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

export type AuditAction = 'view' | 'create' | 'update' | 'delete' | 'export' | 'print' | 'login' | 'logout' | 'login_failed' | 'inquiry_created' | 'invite_created' | 'invite_accepted';
// 'eligibility' entries are written server-side by /api/verify-eligibility.
export type AuditResourceType = 'patient' | 'appointment' | 'note' | 'psychotherapy_note' | 'form' | 'recording' | 'session' | 'auth' | 'inquiry' | 'invite' | 'eligibility';

export interface AuditEntry {
  id?: string;
  timestamp: any; // serverTimestamp
  userId: string;
  userRole: 'provider' | 'client' | 'admin';
  action: AuditAction;
  resourceType: AuditResourceType;
  resourceId: string;
  resourceOwner: string; // The patient's ID (for PHI access tracking)
  ipAddress?: string;
  userAgent?: string;
  details?: string;
}

export class AuditService {
  private static COLLECTION = 'audit_trail';
  private static FAILED_LOGINS_COLLECTION = 'failed_logins';
  private static BLOCKED_IPS_COLLECTION = 'blocked_ips';

  /**
   * Log an audit entry. This is write-only — entries cannot be modified or deleted
   * (enforced by Firestore security rules).
   */
  static async log(entry: Omit<AuditEntry, 'id' | 'timestamp' | 'userAgent'>): Promise<void> {
    try {
      await addDoc(collection(db, this.COLLECTION), {
        ...entry,
        timestamp: serverTimestamp(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      });
    } catch (error) {
      // Audit logging should never block the main flow
      console.error('Audit log failed:', error);
    }
  }

  /**
   * Log PHI access (viewing a patient record).
   */
  static async logPhiAccess(
    userId: string,
    userRole: 'provider' | 'client',
    resourceType: AuditResourceType,
    resourceId: string,
    resourceOwner: string,
    details?: string
  ): Promise<void> {
    await this.log({
      userId,
      userRole,
      action: 'view',
      resourceType,
      resourceId,
      resourceOwner,
      details,
    });
  }

  /**
   * Log PHI modification (creating/updating a patient record).
   */
  static async logPhiModification(
    userId: string,
    userRole: 'provider' | 'client',
    action: 'create' | 'update' | 'delete',
    resourceType: AuditResourceType,
    resourceId: string,
    resourceOwner: string,
    details?: string
  ): Promise<void> {
    await this.log({
      userId,
      userRole,
      action,
      resourceType,
      resourceId,
      resourceOwner,
      details,
    });
  }

  /**
   * Log authentication event.
   */
  static async logAuthEvent(
    userId: string,
    action: 'login' | 'logout',
    userRole: 'provider' | 'client' | 'admin'
  ): Promise<void> {
    await this.log({
      userId,
      userRole,
      action,
      resourceType: 'auth',
      resourceId: userId,
      resourceOwner: userId,
      details: `User ${action}`,
    });
  }

  /**
   * Log a failed login attempt. After 5 failures from the same identifier
   * in 15 minutes, the IP/identifier is blocked.
   */
  static async logFailedLogin(
    identifier: string, // email or phone
    ipAddress?: string
  ): Promise<{ blocked: boolean; attemptsRemaining: number }> {
    try {
      // Record the failed attempt
      await addDoc(collection(db, this.FAILED_LOGINS_COLLECTION), {
        identifier,
        ipAddress: ipAddress || 'unknown',
        timestamp: serverTimestamp(),
      });

      // Count recent failures for this identifier
      const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
      const q = query(
        collection(db, this.FAILED_LOGINS_COLLECTION),
        where('identifier', '==', identifier),
        where('timestamp', '>=', Timestamp.fromDate(fifteenMinutesAgo))
      );
      const snapshot = await getDocs(q);
      const failureCount = snapshot.size;

      if (failureCount >= 5) {
        // Block this identifier
        await addDoc(collection(db, this.BLOCKED_IPS_COLLECTION), {
          identifier,
          ipAddress: ipAddress || 'unknown',
          blockedAt: serverTimestamp(),
          expiresAt: Timestamp.fromDate(new Date(Date.now() + 30 * 60 * 1000)), // 30 min block
          reason: `${failureCount} failed login attempts in 15 minutes`,
        });

        // Log to audit trail
        await this.log({
          userId: 'system',
          userRole: 'admin',
          action: 'login_failed',
          resourceType: 'auth',
          resourceId: identifier,
          resourceOwner: identifier,
          details: `Account locked after ${failureCount} failed attempts`,
          ipAddress,
        });

        return { blocked: true, attemptsRemaining: 0 };
      }

      return { blocked: false, attemptsRemaining: 5 - failureCount };
    } catch (error) {
      console.error('Error logging failed login:', error);
      return { blocked: false, attemptsRemaining: 5 };
    }
  }

  /**
   * Check if an identifier is currently blocked.
   */
  static async isBlocked(identifier: string): Promise<boolean> {
    try {
      const now = Timestamp.now();
      const q = query(
        collection(db, this.BLOCKED_IPS_COLLECTION),
        where('identifier', '==', identifier),
        where('expiresAt', '>', now),
        limit(1)
      );
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking block status:', error);
      return false;
    }
  }

  /**
   * Get recent audit entries for a specific resource (admin use).
   */
  static async getAuditTrail(
    resourceType: AuditResourceType,
    resourceId: string,
    maxEntries: number = 50
  ): Promise<AuditEntry[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('resourceType', '==', resourceType),
        where('resourceId', '==', resourceId),
        orderBy('timestamp', 'desc'),
        limit(maxEntries)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate() || new Date(),
      })) as AuditEntry[];
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      return [];
    }
  }

  /**
   * Get all audit entries for a user (for compliance reporting).
   */
  static async getUserAuditTrail(
    userId: string,
    maxEntries: number = 100
  ): Promise<AuditEntry[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(maxEntries)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate() || new Date(),
      })) as AuditEntry[];
    } catch (error) {
      console.error('Error fetching user audit trail:', error);
      return [];
    }
  }

  /**
   * Get all audit entries across all team members (for System Admin activity view).
   * Returns the most recent entries regardless of userId.
   */
  static async getAllAuditTrail(maxEntries: number = 200): Promise<AuditEntry[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        orderBy('timestamp', 'desc'),
        limit(maxEntries)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate() || new Date(),
      })) as AuditEntry[];
    } catch (error) {
      console.error('Error fetching all audit trail:', error);
      return [];
    }
  }
}
